import React, { useEffect, useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import {
  Phone, MessageCircle, ArrowLeft, ChevronLeft, ChevronRight,
  AlertCircle, Loader2, ArrowUpRight, Check, ChevronDown,
  Banknote, RefreshCw, FileText, Car as CarIcon, X, ZoomIn,
} from 'lucide-react';
import { dealerPublic, buildImageUrl } from '../../services/dealerApi';
import { sortedPhotos, photoUrl, heroPhotoUrl } from '../../utils/dealerPhoto';
import BuyerLeadModal from '../../components/dealers/BuyerLeadModal';
import ShareLinkButton from '../../components/dealers/ShareLinkButton';
import { useBuyerLead } from '../../hooks/useBuyerLead';
import SEO from '../../components/SEO';
import { SPEC_FIELDS, GROUP_TITLES, GROUP_ORDER, groupItems, formatSpec } from '../../utils/vehicleFeatures';

const FeaturesAccordion = ({ features }) => {
  const [open, setOpen] = useState(GROUP_ORDER[0]);
  return (
    <section data-testid="all-features-section" className="bg-white border border-zinc-200 p-5 sm:p-7 md:p-9">
      <div className="font-mono-tech text-[10px] uppercase tracking-[0.3em] text-[#0A3622] mb-3">Detailed Spec Sheet</div>
      <h2 className="font-display text-xl sm:text-2xl md:text-3xl font-black mb-5 tracking-tight">View All Features</h2>
      <div className="space-y-2">
        {GROUP_ORDER.map((g) => {
          const items = groupItems(g, features[g] || {});
          const present = items.filter((i) => i.isOn);
          if (present.length === 0) return null;
          const isOpen = open === g;
          return (
            <div key={g} className="border border-zinc-200">
              <button
                type="button"
                onClick={() => setOpen(isOpen ? null : g)}
                data-testid={`accordion-${g}`}
                className="w-full flex items-center justify-between px-4 sm:px-5 py-3 sm:py-4 bg-[#F8F8F5] hover:bg-zinc-100 text-left"
              >
                <span className="font-display text-sm sm:text-base font-bold text-zinc-900">{GROUP_TITLES[g]}</span>
                <span className="flex items-center gap-2 sm:gap-3 text-xs text-zinc-500">
                  <span className="font-mono-tech tracking-[0.2em]">{present.length}</span>
                  <ChevronDown className={`w-4 h-4 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
                </span>
              </button>
              {isOpen && (
                <div className="px-4 sm:px-5 py-4 grid grid-cols-1 sm:grid-cols-2 gap-2 bg-white">
                  {present.map((it) => (
                    <div key={it.key} className="flex items-center gap-2 text-sm text-zinc-700">
                      <Check className="w-3.5 h-3.5 text-[#0A3622] shrink-0" strokeWidth={3} />
                      <span>{it.label}{it.valueLabel ? ` · ${it.valueLabel}` : ''}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </section>
  );
};

/* -------------------- Lightbox (click-to-expand zoom viewer) -------------------- */
const Lightbox = ({ src, alt, index, total, zoom, onToggleZoom, onPrev, onNext, onClose }) => {
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape') onClose();
      else if (e.key === 'ArrowLeft' && total > 1) onPrev();
      else if (e.key === 'ArrowRight' && total > 1) onNext();
    };
    document.addEventListener('keydown', onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [onClose, onPrev, onNext, total]);

  return (
    <div
      data-testid="photo-lightbox"
      className="fixed inset-0 z-[100] bg-black/95 backdrop-blur-sm flex flex-col"
      onClick={onClose}
    >
      {/* Top bar */}
      <div className="flex items-center justify-between px-4 sm:px-6 py-3 sm:py-4" onClick={(e) => e.stopPropagation()}>
        <div className="font-mono-tech text-[10px] sm:text-xs tracking-[0.22em] text-white/70">
          {total > 1 ? `${String(index + 1).padStart(2, '0')} / ${String(total).padStart(2, '0')}` : 'PHOTO'}
        </div>
        <button
          onClick={onClose}
          data-testid="lightbox-close"
          className="w-10 h-10 sm:w-11 sm:h-11 bg-white/10 hover:bg-white/20 ring-1 ring-white/20 text-white inline-flex items-center justify-center transition-colors"
          aria-label="Close"
        >
          <X className="w-5 h-5" />
        </button>
      </div>

      {/* Image area */}
      <div
        className={`flex-1 min-h-0 flex items-center justify-center px-2 sm:px-6 ${zoom ? 'overflow-auto cursor-zoom-out' : 'overflow-hidden cursor-zoom-in'}`}
        onClick={(e) => { e.stopPropagation(); onToggleZoom(); }}
      >
        <img
          src={src}
          alt={alt}
          className={
            zoom
              ? 'max-w-none max-h-none w-auto h-auto select-none'
              : 'max-w-full max-h-full object-contain select-none'
          }
          draggable={false}
          style={zoom ? { transform: 'scale(2)', transformOrigin: 'center center' } : undefined}
        />
      </div>

      {/* Bottom controls */}
      <div className="flex items-center justify-between gap-3 px-4 sm:px-6 py-3 sm:py-4 safe-bottom" onClick={(e) => e.stopPropagation()}>
        {total > 1 ? (
          <>
            <button
              onClick={onPrev}
              data-testid="lightbox-prev"
              className="inline-flex items-center gap-2 px-4 sm:px-5 py-2.5 sm:py-3 bg-white/10 hover:bg-white/20 ring-1 ring-white/20 text-white text-[10px] sm:text-xs font-mono-tech uppercase tracking-[0.18em] transition-colors"
            >
              <ChevronLeft className="w-4 h-4" /> Prev
            </button>
            <button
              onClick={(e) => { e.stopPropagation(); onToggleZoom(); }}
              data-testid="lightbox-zoom"
              className="inline-flex items-center gap-2 px-4 sm:px-5 py-2.5 sm:py-3 bg-white/10 hover:bg-white/20 ring-1 ring-white/20 text-white text-[10px] sm:text-xs font-mono-tech uppercase tracking-[0.18em] transition-colors"
            >
              <ZoomIn className="w-4 h-4" /> {zoom ? 'Fit' : 'Zoom'}
            </button>
            <button
              onClick={onNext}
              data-testid="lightbox-next"
              className="inline-flex items-center gap-2 px-4 sm:px-5 py-2.5 sm:py-3 bg-white/10 hover:bg-white/20 ring-1 ring-white/20 text-white text-[10px] sm:text-xs font-mono-tech uppercase tracking-[0.18em] transition-colors"
            >
              Next <ChevronRight className="w-4 h-4" />
            </button>
          </>
        ) : (
          <div className="flex-1 flex justify-center">
            <button
              onClick={(e) => { e.stopPropagation(); onToggleZoom(); }}
              data-testid="lightbox-zoom"
              className="inline-flex items-center gap-2 px-5 py-3 bg-white/10 hover:bg-white/20 ring-1 ring-white/20 text-white text-xs font-mono-tech uppercase tracking-[0.18em] transition-colors"
            >
              <ZoomIn className="w-4 h-4" /> {zoom ? 'Fit' : 'Zoom'}
            </button>
          </div>
        )}
      </div>
    </div>
  );
};


const PublicCarDetail = () => {
  const { slug: paramSlug, listingId } = useParams();
  const slug = paramSlug || (typeof window !== 'undefined' && window.__DEALER_HOST_SLUG__) || '';
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeIdx, setActiveIdx] = useState(0);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxZoom, setLightboxZoom] = useState(false);

  useEffect(() => {
    dealerPublic.listing(slug, listingId)
      .then((res) => setData(res))
      .catch(() => setData({ notFound: true }))
      .finally(() => setLoading(false));
  }, [slug, listingId]);

  const lead = useBuyerLead({ slug, dealer: data?.dealer });

  // Replace UUID URL with canonical slug URL (acts as a soft 301 for crawlers
  // hitting the legacy /cars/<uuid> path).
  useEffect(() => {
    if (!data?.listing) return;
    const canonicalSlug = data.listing.slug;
    if (canonicalSlug && listingId !== canonicalSlug) {
      navigate(`/dealers/${slug}/cars/${canonicalSlug}`, { replace: true });
    }
  }, [data, slug, listingId, navigate]);

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F4F4F5] flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-[#0A3622] animate-spin" />
      </div>
    );
  }
  if (!data || data.notFound) {
    return (
      <div className="min-h-screen bg-[#F4F4F5] flex items-center justify-center text-center px-4">
        <div>
          <h1 className="font-display text-3xl font-extrabold mb-2">Car not found</h1>
          <Link to={`/dealers/${slug}`} className="text-xs uppercase tracking-[0.2em] font-bold text-[#0A3622] hover:underline">
            Back to dealer
          </Link>
        </div>
      </div>
    );
  }

  const { listing, dealer } = data;
  const related = data.related || [];
  const photos = sortedPhotos(listing.photos || []);
  const hero = photos[activeIdx] || photos[0];
  const heroSrc = photoUrl(hero);

  const onWhatsApp = () => lead.openFor({ action: 'whatsapp', listing });
  const onCall = () => lead.openFor({ action: 'call', listing });

  // Detect whether the visitor arrived here from the umbrella Propitstop
  // site (a card on /used-cars stamps this flag on click). When true we
  // hide the back-links that would drop them onto the dealer's microsite
  // — otherwise the customer wanders off from the aggregator and gets
  // stuck. Session-scoped so refreshes preserve context.
  const fromPropitstop = (() => {
    try { return sessionStorage.getItem('propitstop_ctx') === '1'; } catch (e) { return false; }
  })();

  const make = listing.ai_details?.make || '';
  const model = listing.ai_details?.model || '';
  const year = listing.year || '';
  const fuel = listing.fuel_type || '';
  const carTitleShort = `${year} ${make} ${model}`.trim();

  // SEO meta + JSON-LD
  const origin = typeof window !== 'undefined' ? window.location.origin : '';
  const canonicalUrl = `${origin}/dealers/${slug}/cars/${listing.slug || listing.id}`;
  const ogImage = heroSrc && heroSrc.startsWith('http') ? heroSrc : (heroSrc ? `${origin}${heroSrc}` : '');
  const seoTitle = `${carTitleShort} ${fuel ? '· ' + fuel + ' ' : ''}for sale in ${dealer.city || 'India'} — ${dealer.name}`.slice(0, 70);
  const seoDesc = `Buy a certified pre-owned ${carTitleShort}${listing.km_driven ? ` · ${listing.km_driven.toLocaleString('en-IN')} km` : ''}${listing.price ? ` · ₹${listing.price.toLocaleString('en-IN')}` : ''}. Sold by ${dealer.name}${dealer.city ? ', ' + dealer.city : ''}.`.slice(0, 200);
  const dealerLogoAbs = dealer.logo_url
    ? (dealer.logo_url.startsWith('http') ? dealer.logo_url : buildImageUrl(dealer.logo_url))
    : null;

  const vehicleJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'Vehicle',
    name: `${make} ${model} ${year}`.trim(),
    brand: make ? { '@type': 'Brand', name: make } : undefined,
    model: model || undefined,
    vehicleModelDate: year ? String(year) : undefined,
    fuelType: fuel || undefined,
    vehicleTransmission: listing.ai_details?.transmission || undefined,
    bodyType: listing.ai_details?.body_type || undefined,
    color: listing.ai_details?.detected_color || undefined,
    image: ogImage || undefined,
    description: listing.description || undefined,
    mileageFromOdometer: listing.km_driven
      ? { '@type': 'QuantitativeValue', value: listing.km_driven, unitCode: 'KMT' }
      : undefined,
    offers: listing.price ? {
      '@type': 'Offer',
      priceCurrency: 'INR',
      price: String(listing.price),
      availability: 'https://schema.org/InStock',
      url: canonicalUrl,
      seller: { '@type': 'AutoDealer', name: dealer.name, ...(dealer.city ? { address: { '@type': 'PostalAddress', addressLocality: dealer.city, addressCountry: 'IN' } } : {}) },
    } : undefined,
  };
  // Strip undefined keys
  Object.keys(vehicleJsonLd).forEach((k) => vehicleJsonLd[k] === undefined && delete vehicleJsonLd[k]);

  const breadcrumbJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: dealer.name, item: `${origin}/dealers/${slug}` },
      { '@type': 'ListItem', position: 2, name: carTitleShort || 'Car', item: canonicalUrl },
    ],
  };

  const ownershipLabel = (n) => {
    if (!n) return null;
    if (n >= 5) return '5+ owners';
    const suf = { 1: 'st', 2: 'nd', 3: 'rd' }[n] || 'th';
    return `${n}${suf} owner`;
  };

  const specs = [
    { label: 'Year', value: listing.year },
    { label: 'Fuel', value: listing.fuel_type },
    listing.km_driven
      ? { label: 'KM Driven', value: listing.km_driven.toLocaleString('en-IN') + ' km' }
      : null,
    listing.ownership ? { label: 'Owners', value: ownershipLabel(listing.ownership) } : null,
    listing.ai_details?.transmission ? { label: 'Trans.', value: listing.ai_details.transmission } : null,
    listing.ai_details?.body_type ? { label: 'Body', value: listing.ai_details.body_type } : null,
    listing.ai_details?.detected_color ? { label: 'Color', value: listing.ai_details.detected_color } : null,
  ].filter(Boolean);

  const damageColor = (s) =>
    s === 'major' ? 'border-red-300 bg-red-50 text-red-900' :
    s === 'moderate' ? 'border-amber-300 bg-amber-50 text-amber-900' :
    'border-yellow-300 bg-yellow-50 text-yellow-900';

  // ----- Vehicle Intelligence (specs / features / highlights) -----
  const intel = listing.intelligence || null;
  const vehicleSpecs = intel?.specifications || {};
  const features = intel?.features || {};
  const hasFeatures = Object.values(features).some((g) => g && Object.keys(g).length > 0);
  const allHighlights = intel?.key_highlights || [];

  const trustItems = [
    dealer.trust_finance_assistance !== false && { icon: Banknote, label: 'Finance Assistance Available' },
    dealer.trust_exchange_assistance !== false && { icon: RefreshCw, label: 'Exchange Assistance Available' },
    dealer.trust_documentation_support !== false && { icon: FileText, label: 'Documentation Support' },
    dealer.trust_test_drive_available !== false && { icon: CarIcon, label: 'Test Drive Available' },
  ].filter(Boolean);

  const template = dealer.template === 'marketplace' ? 'luxury' : (dealer.template || 'clean_white');

  return (
    <div data-dealer-skin data-template={template} className="min-h-screen bg-[#F4F4F5] text-zinc-900 pb-40 sm:pb-0 overflow-x-hidden">
      <SEO
        title={seoTitle}
        description={seoDesc}
        canonical={canonicalUrl}
        image={ogImage}
        favicon={dealerLogoAbs}
        type="product"
        jsonLd={[vehicleJsonLd, breadcrumbJsonLd]}
      />
      {/* Umbrella breadcrumb REMOVED (user feedback Aug 8): visually
          intrusive on the dealer microsite. Context-aware back button in
          the sticky top-bar already handles navigation — 'Back to Used
          Cars' for Propitstop visitors, 'Back to <Dealer>' otherwise. */}

      {/* Top bar */}
      <header className="sticky top-0 z-40 bg-white/85 backdrop-blur-md border-b border-zinc-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 md:px-12 lg:px-24 h-14 sm:h-16 flex items-center justify-between gap-2">
          {fromPropitstop ? (
            // Arrived via the Propitstop aggregator — keep them inside it.
            // Show a "Back to Used Cars" link instead of dropping them onto
            // the dealer's microsite.
            <Link
              to="/used-cars"
              data-testid="back-to-used-cars"
              className="inline-flex items-center gap-2 text-[10px] sm:text-xs uppercase tracking-[0.18em] sm:tracking-[0.2em] font-bold text-zinc-700 hover:text-blue-600 truncate min-w-0"
            >
              <ArrowLeft className="w-4 h-4 shrink-0" /> <span className="truncate">Back to Used Cars</span>
            </Link>
          ) : (
            <button
              onClick={() => navigate(`/dealers/${slug}`)}
              data-testid="back-to-dealer"
              className="inline-flex items-center gap-2 text-[10px] sm:text-xs uppercase tracking-[0.18em] sm:tracking-[0.2em] font-bold text-zinc-700 hover:text-[#0A3622] truncate min-w-0"
            >
              <ArrowLeft className="w-4 h-4 shrink-0" /> <span className="truncate">{dealer.name}</span>
            </button>
          )}
          <div className="hidden sm:flex items-center gap-2">
            {dealer.contact_number && (
              <button
                onClick={onCall}
                data-testid="topbar-call"
                className="inline-flex items-center gap-2 px-4 py-2 border border-zinc-300 text-xs uppercase tracking-[0.18em] font-bold text-zinc-700 hover:bg-white"
              >
                <Phone className="w-3.5 h-3.5" /> Call Now
              </button>
            )}
            <button
              onClick={onWhatsApp}
              data-testid="topbar-whatsapp"
              className="inline-flex items-center gap-2 px-4 py-2 bg-[#25D366] hover:bg-[#22C55E] text-white text-xs uppercase tracking-[0.18em] font-bold"
            >
              <MessageCircle className="w-3.5 h-3.5" /> WhatsApp
            </button>
            <ShareLinkButton
              slug={slug}
              listingId={listing.slug || listing.id}
              title={`${carTitleShort} · ${dealer.name}`}
              text={`${carTitleShort}${listing.price ? ' · ₹' + listing.price.toLocaleString('en-IN') : ''} — ${dealer.name}`}
              variant="icon"
              testId="topbar-share"
            />
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 md:px-12 lg:px-24 py-5 sm:py-8 md:py-12">
        <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,1fr),400px] gap-6 sm:gap-10 lg:gap-14">
          {/* LEFT — Gallery + Details */}
          <div className="space-y-5 sm:space-y-8">
            {/* Cinematic Gallery */}
            <div className="bg-zinc-900 overflow-hidden">
              <div className="relative aspect-[16/10] group">
                {heroSrc && (
                  <button
                    type="button"
                    onClick={() => { setLightboxZoom(false); setLightboxOpen(true); }}
                    data-testid="gallery-open-lightbox"
                    className="block w-full h-full cursor-zoom-in"
                    aria-label="View full screen"
                  >
                    <img
                      src={heroSrc}
                      alt={`${carTitleShort} ${fuel} for sale at ${dealer.name}`}
                      fetchpriority="high"
                      decoding="async"
                      className="w-full h-full object-cover"
                    />
                  </button>
                )}
                {hero?.is_enhanced && (
                  <span className="absolute top-3 left-3 sm:top-4 sm:left-4 inline-flex items-center gap-1.5 px-2 py-1 bg-black/80 backdrop-blur text-[9px] sm:text-[10px] font-mono-tech uppercase tracking-[0.22em] text-white pointer-events-none">
                    <span className="w-1.5 h-1.5 rounded-full bg-red-500 animate-pulse" /> Studio
                  </span>
                )}
                <span className="absolute top-3 right-3 sm:top-4 sm:right-4 inline-flex items-center gap-1.5 px-2 py-1 bg-black/60 backdrop-blur text-[9px] sm:text-[10px] font-mono-tech uppercase tracking-[0.22em] text-white pointer-events-none">
                  <ZoomIn className="w-3 h-3" /> Tap to expand
                </span>
              </div>
              {/* Bottom control bar — prev / counter / next */}
              {photos.length > 1 && (
                <div className="flex items-center justify-between gap-3 px-3 sm:px-4 py-2.5 sm:py-3 bg-zinc-900 border-t border-white/5">
                  <button
                    onClick={() => setActiveIdx((i) => (i - 1 + photos.length) % photos.length)}
                    className="inline-flex items-center gap-1.5 px-3 sm:px-4 py-2 bg-white/10 hover:bg-white/20 backdrop-blur ring-1 ring-white/20 text-white text-[10px] sm:text-xs font-mono-tech uppercase tracking-[0.18em] transition-colors"
                    data-testid="gallery-prev"
                  >
                    <ChevronLeft className="w-4 h-4" /> Prev
                  </button>
                  <div className="font-mono-tech text-[10px] sm:text-xs tracking-[0.22em] text-white/80">
                    {String(activeIdx + 1).padStart(2, '0')} / {String(photos.length).padStart(2, '0')}
                  </div>
                  <button
                    onClick={() => setActiveIdx((i) => (i + 1) % photos.length)}
                    className="inline-flex items-center gap-1.5 px-3 sm:px-4 py-2 bg-white/10 hover:bg-white/20 backdrop-blur ring-1 ring-white/20 text-white text-[10px] sm:text-xs font-mono-tech uppercase tracking-[0.18em] transition-colors"
                    data-testid="gallery-next"
                  >
                    Next <ChevronRight className="w-4 h-4" />
                  </button>
                </div>
              )}
              {photos.length > 1 && (
                <div className="px-2 sm:px-3 py-2 sm:py-3 flex gap-2 overflow-x-auto bg-zinc-900/95">
                  {photos.map((p, i) => (
                    <button
                      key={p.url}
                      onClick={() => setActiveIdx(i)}
                      className={`relative shrink-0 w-20 h-14 sm:w-24 sm:h-16 overflow-hidden transition-all ${
                        i === activeIdx
                          ? 'ring-2 ring-[#D4AF37] opacity-100'
                          : 'opacity-50 grayscale hover:grayscale-0 hover:opacity-100'
                      }`}
                      data-testid={`thumb-${i}`}
                    >
                      <img src={photoUrl(p)} alt="" loading="lazy" decoding="async" className="w-full h-full object-cover" />
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Title block */}
            <div className="bg-white border border-zinc-200 p-5 sm:p-7 md:p-9">
              <div className="flex flex-wrap items-end justify-between gap-3 mb-2">
                <div className="font-mono-tech text-[10px] uppercase tracking-[0.3em] text-[#0A3622]">
                  {listing.year} · {listing.fuel_type}
                </div>
                <div className="lg:hidden">
                  <div className="font-display text-xl sm:text-2xl font-black tracking-tight">
                    ₹{(listing.price || 0).toLocaleString('en-IN')}
                  </div>
                </div>
              </div>
              <h1 className="font-display text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-black leading-[0.95] tracking-[-0.03em] break-words">
                {listing.ai_details?.make} {listing.ai_details?.model}
              </h1>
              {listing.ai_details?.variant && (
                <p className="mt-2 sm:mt-3 text-zinc-500 text-sm sm:text-base">{listing.ai_details.variant}</p>
              )}

              {/* Spec tiles — technical crosshair grid */}
              <div className="mt-6 sm:mt-8 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 border border-zinc-200">
                {specs.map((s, i) => (
                  <div
                    key={s.label}
                    className={`p-3 sm:p-4 ${i !== 0 ? 'border-l border-zinc-200' : ''} ${
                      i >= 2 ? 'border-t' : ''
                    } ${i % 2 === 0 ? '!border-l-0 sm:!border-l' : ''} ${i % 3 === 0 ? 'sm:!border-l-0' : ''} ${i % 6 === 0 ? 'lg:!border-l-0' : ''} ${i >= 3 ? 'sm:!border-t-0' : ''} ${i >= 6 ? 'lg:!border-t-0' : ''}`}
                  >
                    <div className="font-mono-tech text-[9px] uppercase tracking-[0.28em] text-zinc-400 mb-1.5">{s.label}</div>
                    <div className="font-display text-sm sm:text-base font-bold text-zinc-900 leading-tight">{s.value}</div>
                  </div>
                ))}
              </div>
            </div>

            {/* Description */}
            {listing.description && (
              <div className="bg-white border border-zinc-200 p-5 sm:p-7 md:p-9">
                <div className="font-mono-tech text-[10px] uppercase tracking-[0.3em] text-[#0A3622] mb-3">About</div>
                <h2 className="font-display text-xl sm:text-2xl md:text-3xl font-black mb-3 sm:mb-4 tracking-tight">About this car</h2>
                <p className="text-sm sm:text-base text-zinc-700 leading-relaxed whitespace-pre-line">{listing.description}</p>
                {listing.condition_summary && (
                  <div className="mt-4 sm:mt-5 inline-flex items-start gap-2 px-3 py-2 bg-[#0A3622] text-white text-[10px] uppercase tracking-[0.22em] font-bold leading-snug max-w-full">
                    <Check className="w-3 h-3 mt-0.5 shrink-0" strokeWidth={3} />
                    <span className="break-words min-w-0">{listing.condition_summary}</span>
                  </div>
                )}
              </div>
            )}

            {/* Key Highlights (max 8 desktop, 6 mobile) */}
            {allHighlights.length > 0 && (
              <section data-testid="key-highlights-section" className="bg-white border border-zinc-200 p-5 sm:p-7 md:p-9">
                <div className="font-mono-tech text-[10px] uppercase tracking-[0.3em] text-[#0A3622] mb-3">Top Picks</div>
                <h2 className="font-display text-xl sm:text-2xl md:text-3xl font-black mb-5 tracking-tight">Key Highlights</h2>
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2.5 sm:gap-3">
                  {allHighlights.slice(0, 8).map((h, i) => (
                    <div
                      key={h.key}
                      data-testid={`highlight-${h.key}`}
                      className={`flex items-center gap-2 px-3 py-3 border border-zinc-200 bg-[#F8F8F5] hover:border-[#0A3622] transition-colors ${
                        i >= 6 ? 'hidden sm:flex' : ''
                      }`}
                    >
                      <span className="shrink-0 w-6 h-6 inline-flex items-center justify-center bg-[#0A3622] text-white">
                        <Check className="w-3 h-3" strokeWidth={3} />
                      </span>
                      <span className="text-xs sm:text-sm font-semibold text-zinc-800 leading-tight">{h.label}</span>
                    </div>
                  ))}
                </div>
              </section>
            )}

            {/* Vehicle Specifications */}
            {intel && (
              <section data-testid="vehicle-specs-section" className="bg-white border border-zinc-200 p-5 sm:p-7 md:p-9">
                <div className="font-mono-tech text-[10px] uppercase tracking-[0.3em] text-[#0A3622] mb-3">Under the Hood</div>
                <h2 className="font-display text-xl sm:text-2xl md:text-3xl font-black mb-5 tracking-tight">Vehicle Specifications</h2>
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 border border-zinc-200">
                  {SPEC_FIELDS.map((f, i) => {
                    const val = formatSpec(vehicleSpecs[f.key], f.suffix || '');
                    if (!val) return null;
                    return (
                      <div
                        key={f.key}
                        data-testid={`spec-${f.key}`}
                        className={`p-3 sm:p-4 ${i !== 0 ? 'border-l border-zinc-200' : ''} ${i >= 2 ? 'border-t' : ''}`}
                      >
                        <div className="font-mono-tech text-[9px] uppercase tracking-[0.24em] text-zinc-400 mb-1.5">{f.label}</div>
                        <div className="font-display text-sm sm:text-base font-bold text-zinc-900 leading-tight">{val}</div>
                      </div>
                    );
                  })}
                </div>
              </section>
            )}

            {/* View All Features — expandable accordion */}
            {hasFeatures && (
              <FeaturesAccordion features={features} />
            )}

            {/* Mobile Dealer Trust — desktop shows it in the right sidebar */}
            {trustItems.length > 0 && (
              <div data-testid="dealer-trust-mobile" className="lg:hidden bg-white border border-zinc-200 p-5 sm:p-7">
                <div className="font-mono-tech text-[10px] uppercase tracking-[0.3em] text-[#0A3622] mb-2">Why Buy From This Dealer</div>
                <h3 className="font-display text-lg sm:text-xl font-black text-zinc-900 mb-4">Dealer Services</h3>
                <ul className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {trustItems.map((t) => (
                    <li key={t.label} className="flex items-start gap-3 text-sm">
                      <span className="shrink-0 w-7 h-7 inline-flex items-center justify-center bg-[#0A3622] text-white">
                        <t.icon className="w-3.5 h-3.5" />
                      </span>
                      <span className="font-semibold text-zinc-800 leading-tight pt-1">{t.label}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* Inspection Report */}
            <div className="bg-white border border-zinc-200 p-5 sm:p-7 md:p-9">
              <div className="flex items-center justify-between flex-wrap gap-3 mb-5 sm:mb-6">
                <div>
                  <div className="font-mono-tech text-[10px] uppercase tracking-[0.3em] text-[#0A3622] mb-1">Quality Assured</div>
                  <h2 className="font-display text-xl sm:text-2xl md:text-3xl font-black tracking-tight">Inspection Report</h2>
                </div>
                <div className="font-mono-tech text-[10px] uppercase tracking-[0.22em] text-zinc-500">
                  AI-Verified · {(listing.damages || []).length} {(listing.damages || []).length === 1 ? 'finding' : 'findings'}
                </div>
              </div>
              {(!listing.damages || listing.damages.length === 0) ? (
                <div className="flex items-start gap-4 p-5 border border-emerald-300 bg-emerald-50">
                  <span className="w-9 h-9 shrink-0 bg-[#0A3622] text-white inline-flex items-center justify-center">
                    <Check className="w-4 h-4" strokeWidth={3} />
                  </span>
                  <div>
                    <p className="font-display font-bold text-emerald-900 text-lg">No visible damage</p>
                    <p className="text-sm text-emerald-800/80 mt-0.5">AI inspection found no visible cosmetic or structural concerns.</p>
                  </div>
                </div>
              ) : (
                <div className="space-y-2">
                  <p className="text-sm text-zinc-600 mb-3">We're transparent about every car. Here's what our inspection found:</p>
                  {listing.damages.map((d, idx) => (
                    <div key={d.id || idx} className={`flex items-start gap-3 p-4 border ${damageColor(d.severity)}`}>
                      <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" />
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="font-mono-tech text-[10px] uppercase tracking-[0.2em] font-bold">{d.severity}</span>
                          <span className="opacity-50">·</span>
                          <span className="text-sm font-bold">{d.location}</span>
                        </div>
                        <p className="text-sm mt-1 opacity-90">{d.description}</p>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* RIGHT — Sticky Price Card (desktop) */}
          <aside className="lg:sticky lg:top-24 lg:self-start space-y-4 hidden lg:block">
            <div className="bg-white border border-zinc-200 p-7">
              <div className="font-mono-tech text-[10px] uppercase tracking-[0.3em] text-zinc-400 mb-1">Price</div>
              <div className="font-display text-4xl xl:text-5xl font-black tracking-[-0.03em] text-zinc-900 leading-none">
                ₹{(listing.price || 0).toLocaleString('en-IN')}
              </div>
              <p className="font-mono-tech text-[10px] uppercase tracking-[0.22em] text-zinc-500 mt-2">
                All-inclusive · No hidden charges
              </p>

              <div className="mt-7 space-y-3">
                <button
                  onClick={onWhatsApp}
                  data-testid="detail-whatsapp"
                  className="group w-full inline-flex items-center justify-between px-5 py-4 bg-[#25D366] hover:bg-[#22C55E] text-white font-bold text-sm uppercase tracking-[0.18em] transition-colors"
                >
                  <span className="inline-flex items-center gap-2"><MessageCircle className="w-4 h-4" /> WhatsApp</span>
                  <ArrowUpRight className="w-4 h-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                </button>
                <button
                  onClick={onCall}
                  data-testid="detail-call"
                  className="group w-full inline-flex items-center justify-between px-5 py-4 bg-[#0A3622] hover:bg-[#072718] text-white font-bold text-sm uppercase tracking-[0.18em] transition-colors"
                >
                  <span className="inline-flex items-center gap-2"><Phone className="w-4 h-4" /> Call dealer</span>
                  <ArrowUpRight className="w-4 h-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                </button>
                <ShareLinkButton
                  slug={slug}
                  listingId={listing.slug || listing.id}
                  title={`${carTitleShort} · ${dealer.name}`}
                  text={`${carTitleShort}${listing.price ? ' · ₹' + listing.price.toLocaleString('en-IN') : ''} — ${dealer.name}`}
                  variant="pill"
                  label="Share this car"
                  testId="detail-share"
                />
              </div>
            </div>

            <div className="bg-white border border-zinc-200 p-6">
              <div className="font-mono-tech text-[10px] uppercase tracking-[0.3em] text-zinc-400 mb-2">Sold by</div>
              <div className="font-display text-xl font-black text-zinc-900">{dealer.name}</div>
              <div className="mt-3 inline-flex items-center gap-1.5 font-mono-tech text-[10px] uppercase tracking-[0.22em] text-[#0A3622]">
                <span className="w-3.5 h-3.5 inline-flex items-center justify-center rounded-full border border-[#0A3622] text-[#0A3622]">
                  <Check className="w-2 h-2" strokeWidth={3} />
                </span>
                Verified Dealer
              </div>
            </div>

            {trustItems.length > 0 && (
              <div data-testid="dealer-trust-card" className="bg-white border border-zinc-200 p-6">
                <div className="font-mono-tech text-[10px] uppercase tracking-[0.3em] text-[#0A3622] mb-1">Why Buy From This Dealer</div>
                <h3 className="font-display text-lg font-black text-zinc-900 mb-4">Dealer Services</h3>
                <ul className="space-y-3">
                  {trustItems.map((t) => (
                    <li key={t.label} className="flex items-start gap-3 text-sm">
                      <span className="shrink-0 w-7 h-7 inline-flex items-center justify-center bg-[#0A3622] text-white">
                        <t.icon className="w-3.5 h-3.5" />
                      </span>
                      <span className="font-semibold text-zinc-800 leading-tight pt-1">{t.label}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            <div className="bg-[#0A3622] text-white p-6">
              <div className="font-mono-tech text-[10px] uppercase tracking-[0.3em] text-[#D4AF37] mb-2">Optional</div>
              <h3 className="font-display text-lg font-black mb-2">Get an expert inspection</h3>
              <p className="text-sm text-white/70 mb-4 leading-relaxed">
                Book a Propitstop expert physical inspection at <strong className="text-white">₹999</strong>.
              </p>
              <button className="w-full inline-flex items-center justify-between px-4 py-3 bg-white text-[#0A3622] text-xs uppercase tracking-[0.18em] font-bold hover:bg-[#F4F4F5] transition-colors">
                <span>Request Inspection</span>
                <ArrowUpRight className="w-4 h-4" />
              </button>
            </div>
          </aside>
        </div>

        {/* RELATED CARS — internal linking + SEO */}
        {related.length > 0 && (
          <section className="mt-14 sm:mt-20" data-testid="related-cars-section">
            <div className="flex items-end justify-between gap-3 mb-6 sm:mb-8 flex-wrap">
              <div>
                <div className="font-mono-tech text-[10px] uppercase tracking-[0.3em] text-[#0A3622] mb-1">Browse more</div>
                <h2 className="font-display text-2xl sm:text-3xl font-black tracking-tight">More cars from {dealer.name}</h2>
              </div>
              {!fromPropitstop && (
                <Link
                  to={`/dealers/${slug}`}
                  className="inline-flex items-center gap-1 font-mono-tech text-[11px] font-bold uppercase tracking-[0.18em] text-[#0A3622] hover:underline"
                >
                  View All <ArrowUpRight className="w-3.5 h-3.5" />
                </Link>
              )}
            </div>
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6">
              {related.map((r) => {
                const rHero = heroPhotoUrl(r.photos);
                const rTitle = `${r.ai_details?.make || ''} ${r.ai_details?.model || ''}`.trim() || 'View car';
                const rPath = r.slug || r.id;
                return (
                  <Link
                    key={r.id}
                    to={`/dealers/${slug}/cars/${rPath}`}
                    className="group block bg-white border border-zinc-200 hover:border-[#0A3622] transition-colors"
                  >
                    <div className="relative aspect-[16/10] overflow-hidden bg-zinc-100">
                      {rHero && (
                        <img
                          src={rHero}
                          alt={`${rTitle} for sale at ${dealer.name}`}
                          loading="lazy"
                          decoding="async"
                          className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-[1.04]"
                        />
                      )}
                    </div>
                    <div className="p-4">
                      <div className="font-display text-base font-bold text-zinc-900">{rTitle}</div>
                      <div className="font-mono-tech text-[10px] uppercase tracking-[0.18em] text-zinc-500 mt-1">
                        {[r.year, r.fuel_type].filter(Boolean).join(' · ')}
                      </div>
                      <div className="mt-3 font-display text-lg font-extrabold text-zinc-900">
                        ₹{(r.price || 0).toLocaleString('en-IN')}
                      </div>
                    </div>
                  </Link>
                );
              })}
            </div>
          </section>
        )}
      </main>

      {/* Mobile sticky bottom CTA — sits above the Emergent badge */}
      <div className="sm:hidden fixed bottom-12 left-0 right-0 z-40 bg-white border-t border-zinc-200 p-3 shadow-[0_-4px_20px_rgba(0,0,0,0.08)]">
        <div className="flex items-center justify-between gap-3">
          <div className="min-w-0">
            <div className="font-mono-tech text-[9px] uppercase tracking-[0.22em] text-zinc-400">Price</div>
            <div className="font-display text-lg font-black text-zinc-900 leading-tight">
              ₹{(listing.price || 0).toLocaleString('en-IN')}
            </div>
          </div>
          <div className="flex gap-2 flex-1 justify-end">
            <button onClick={onCall} className="w-12 h-12 bg-[#0A3622] text-white flex items-center justify-center"><Phone className="w-4 h-4" /></button>
            <button onClick={onWhatsApp} className="flex-1 max-w-[180px] px-4 py-3 bg-[#25D366] text-white font-bold text-xs uppercase tracking-[0.18em] inline-flex items-center justify-center gap-2">
              <MessageCircle className="w-4 h-4" /> WhatsApp
            </button>
          </div>
        </div>
      </div>

      <BuyerLeadModal {...lead.modalProps} />

      {/* Photo Lightbox — full screen zoom viewer */}
      {lightboxOpen && heroSrc && (
        <Lightbox
          src={heroSrc}
          alt={`${carTitleShort} ${fuel}`}
          index={activeIdx}
          total={photos.length}
          zoom={lightboxZoom}
          onToggleZoom={() => setLightboxZoom((z) => !z)}
          onPrev={() => { setLightboxZoom(false); setActiveIdx((i) => (i - 1 + photos.length) % photos.length); }}
          onNext={() => { setLightboxZoom(false); setActiveIdx((i) => (i + 1) % photos.length); }}
          onClose={() => { setLightboxOpen(false); setLightboxZoom(false); }}
        />
      )}
    </div>
  );
};

export default PublicCarDetail;
